/* @flow */
import gql from 'graphql-tag';
import {pick} from 'lodash';
import {fragments as hitCollectionFragments} from '../components/HitCollection/HitCollection';
import {fragments as mapFragments} from '../components/Map/Map';
import {fragments as pagerFragments} from '../components/Pager/Pager';
import {fragments as adSenseFragments} from '../components/Ads/AdSense/AdSenseFragment';
import {fragments as percentBookedContentsFragments} from '../components/SearchBannerContainer/percentBookedContents';
import {fragments as urgentMessageContentsFragments} from '../components/SearchBannerContainer/urgentMessageContents';
import {fragments as footerSeoLinksFragments} from '../components/ConnectedFooterSeoLinks/ConnectedFooterSeoLinks';
import {fragments as filterCountsFragments} from '../../universal/queries/filteredCountQuery';
import {fragments as expandedSearchGroupsFragments} from '../components/ExpandedSearchGroups/ExpandedSearchGroups';
import {fragments as htmlTitleFragments} from '../components/HtmlTitle/HtmlTitle';
import {ResultsParams} from '../../server/graphql/params';

import type {AdSenseConfiguration} from '../components/Ads/AdSense/AdSenseFragment';
import type {SearchParamsType} from '../reducers/searchParams';
import type {FilterGroupType} from '../components/MoreFilters/MoreFilters';
import type {Shape as HitShape} from '../components/Hit/Hit';
import type {MapViewport} from '../components/Map/Map';
import type {Shape as ExpandedSearchGroupsShape} from '../components/ExpandedSearchGroups/ExpandedSearchGroups';

export type SearchReview = {
    detailPageUrl: string,
    reviewHead: string,
    reviewText: string,
    reviewCreated: string,
    reviewArrival: string,
    reviewRating: number,
    hitThumbnailUrl: string
};

export type SearchTerm = {
    description: string,
    name: string,
    termId: string
};

export type Coordinates = {
    latitude: number,
    longitude: number
};

export type SearchGeography = {
    name?: string,
    description?: string,
    geoType?: string,
    location?: Coordinates,
    isGeoCoded?: boolean
};

type Internal = {
    searchServiceUrl: string
};

export type HitCollection = {
    mapViewport?: MapViewport,
    hits?: HitShape[],
    isDatelessSearch?: boolean,
    pageSize?: number,
    page?: number,
    expandedSearchGroups?: ExpandedSearchGroupsShape
};

export type BreadcrumbShape = {
    url: string,
    name: string
};

export type DestinationBreadcrumbShape = {
    name: string,
    url: string
};

export type DestinationShape = {
    breadcrumbs?: BreadcrumbShape[],
    links?: BreadcrumbShape[]
};

export type SearchQueryResults = {
    adSenseConfiguration?: AdSenseConfiguration,
    analyticsdatalayer?: string,
    breadcrumbs?: BreadcrumbShape[],
    destination?: DestinationShape,
    destinationBreadcrumbs?: DestinationBreadcrumbShape[],
    hitCollection?: any,
    headlineTitle?: string,
    internal?: Internal,
    liveRegionRedirectUrl?: string,
    pidRedirectUrl?: string,
    hitCollection?: HitCollection,
    pageTitle?: string,
    metaDescription?: string,
    metaKeywords?: string,
    searchBanner?: any,
    searchTerm?: ?SearchTerm,
    searchReviewsHeadline?: string,
    searchReviews?: SearchReview[],
    seoSerpLinks?: BreadcrumbShape[],
    filterGroups?: FilterGroupType[],
    canonicalLink?: String,
    searchGeography?: SearchGeography
};

export type SearchQueryResponseData = {
    results?: SearchQueryResults,
    error?: Error,
    loading: boolean
};

export const srsParams = (params: SearchParamsType) =>
    pick(params, ResultsParams);

export const searchQueryParams = (
    siteName: string,
    params: SearchParamsType
) => ({
    ...srsParams(params),
    site: siteName,
    include: ['searchBanner']
});

export const FORCE_GRAPHQL_ERROR_HEADER = 'ha-serp-force-graphql-err';

const searchQuery = gql`
    query SearchQuery($params: ResultsParams, $url: String) {
        results(params: $params) {
            id
            canonicalLink(url: $url)
            analyticsdatalayer
            destinationBreadcrumbs {
                name
                url
            }
            headlineTitle
            hitCollection {
                ...ResultsHitCollection
                ...PagerHitCollection
                ...MapHitCollection
                ...ExpandedSearchGroupsHitCollection
            }
            internal {
                searchServiceUrl
            }
            searchBanner {
                type
                ...PercentBookedSearchBanner
                ...UrgentMessageSearchBanner
            }
            breadcrumbs {
                url
                name
                termId
            }
            liveRegionRedirectUrl
            pidRedirectUrl
            searchReviewsHeadline
            searchReviews {
                detailPageUrl
                reviewHead
                reviewText
                reviewCreated
                reviewArrival
                reviewRating
                hitThumbnailUrl
            }
            searchTerm {
                description
                name
                termId
            }
            seoSerpLinks {
                name
                url
                termId
            }
            filterGroups {
                key
                filters {
                    key
                    checked
                }
            }
            searchGeography {
                geoType
                location {
                    latitude
                    longitude
                }
                isGeoCoded
            }
            ...AdSenseResults
            ...FilterCountsResults
            ...FooterSeoLinksResults
            ...HtmlTitleResults
        }
    }
    ${adSenseFragments.results}
    ${expandedSearchGroupsFragments.hitCollection}
    ${filterCountsFragments.results}
    ${footerSeoLinksFragments.results}
    ${hitCollectionFragments.hitCollection}
    ${mapFragments.hitCollection}
    ${pagerFragments.hitCollection}
    ${htmlTitleFragments.results}
    ${percentBookedContentsFragments.searchBanner}
    ${urgentMessageContentsFragments.searchBanner}
`;

export default searchQuery;
